import { HeartIcon } from "@heroicons/react/24/outline"
import React, { useEffect } from "react"
import { usePageContext } from "vike-react/usePageContext"
import useClientAuth from "../hooks/useClientAuth"
import ProductBox from "../ui/ProductBox"
import Chip from "../ui/Chip"
import Spinner from "../ui/Spinner"
import Pagination from "../ui/Pagination"
import Button from "aldoo-ra/Button"
import useT from "../hooks/useT"
import useAsync from "../hooks/useAsync"
import { useContentLanguage } from "../hooks/useSiteLanguage"
import useSearchParams, { setSearchParams } from "../hooks/useSearchParams"
import { usePageTitle } from "../hooks/usePageTitle"
import { useStoreAccount } from "../store/account"
import { ListAccountProducts } from "../storeApi"
import { stripLang } from "../utils/contentRoutes"
import clientRedirect from "../utils/clientRedirect"
import { LIST_FIELDS } from "../api"
import { useSignInGate } from "../utils/signIn"

const TABS = ["products", "history", "favorites"]
const ITEMS_PER_PAGE = 24

/**
 * /library[/:tab] — the signed-in user's products, history and favorites
 * (product {list from_account}). Guests are sent to sign-in.
 */
export default function LibraryPage() {
  const t = useT()
  const language = useContentLanguage()
  const pageContext = usePageContext()
  const params = useSearchParams()
  const { auth } = useClientAuth()
  const { account } = useStoreAccount()
  const segments = stripLang(pageContext.urlPathname).split("/").filter(Boolean)
  const tab = TABS.includes(segments[1]) ? segments[1] : "products"
  const page = Math.max(1, parseInt(params.get("page")) || 1)
  const textSearch = params.get("textSearch") || ""
  const signedIn = !!auth?.auth_token

  const titles = { products: t`My Products`, history: t`History`, favorites: t`Favorite Products` }
  usePageTitle(t`Library`)

  // a guest signs in over this page; closing it goes home
  useSignInGate(auth)
  // old LibraryPage: any other tab redirects to /library
  useEffect(() => {
    if (segments[1] && !TABS.includes(segments[1])) clientRedirect("/library")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segments[1]])

  // the account (owned pids, favorites) is part of the key so a toggle refreshes the list
  const accountTick = `${account?.favorites?.length ?? ""}:${account?.products?.length ?? ""}:${account?.history?.length ?? ""}`
  const { result, pending } = useAsync(
    () =>
      ListAccountProducts({
        page: page - 1,
        items_per_page: ITEMS_PER_PAGE,
        language,
        text_search: textSearch || undefined,
        from_account: tab,
        fields: LIST_FIELDS,
        filterParams: tab === "history" ? { excludePhysicalProducts: true } : undefined,
      }),
    [tab, page, language, textSearch, accountTick, signedIn],
    { enabled: signedIn }
  )

  const list = result?.list || []
  const pageCount = result?.pages || 1
  const loadError = result?.error
  // old scrollToTop() once the request settled
  useEffect(() => {
    if (!result || typeof window === "undefined") return
    window.scrollTo({ top: 0 })
  }, [result])

  return (
    <div className="w-full max-w-[1360px] mx-auto px-4 md:px-6 pt-10 pb-24 flex flex-col gap-6" data-testid="library-page">
      <h1 className="m-0 text-[32px] md:text-[44px] font-bold text-center sm:text-left">{titles[tab]}</h1>
      <div className="flex flex-row flex-wrap items-center gap-2" role="tablist">
        {TABS.map((id) => (
          <Chip
            key={id}
            to={id === "products" ? "/library" : `/library/${id}`}
            label={id === "products" ? t`Products` : id === "history" ? t`History` : t`Favorites`}
            tone={tab === id ? "dark" : "outline"}
          />
        ))}
        <Button variant="none" href="/category/latest" className="md:ml-auto inline-flex h-[40px] items-center rounded-full bg-primary px-5 text-[16px] font-bold text-white hover:bg-primary_hov" data-testid="library-add-product">
          {t`Add a Product`}
        </Button>
        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            setSearchParams({ textSearch: new FormData(e.currentTarget).get("q"), page: null })
          }}
        >
          <input name="q" defaultValue={textSearch} placeholder={t`Search`} className="h-[40px] w-[200px] rounded-full border border-bordered px-4 text-[15px] outline-none focus:border-text" />
        </form>
      </div>

      {!signedIn && <Spinner className="py-24" />}
      {signedIn && pending && !result && <Spinner className="py-24" />}
      {loadError && <p className="m-0 rounded-lg bg-error/10 px-4 py-3 text-[14px] text-error">{t`Could not load your library. Please try again.`}</p>}
      {signedIn && result && !loadError && list.length === 0 && (
        <div className="mt-[78px] sm:mt-[128px] flex w-full max-w-[520px] flex-col items-center gap-3 self-center text-center" data-testid="library-empty">
          {/* what each tab is for, said when it has nothing to show */}
          {tab === "favorites" && (
            <>
              <HeartIcon className="w-10 h-10 text-text_secondary" />
              <p className="m-0 text-[20px] font-semibold">{t`Your favorites list is empty`}</p>
              <p className="m-0 text-[15px] text-text_secondary">{t`Tap the heart on any product to save it here: come back to it later, compare, and buy when you are ready.`}</p>
            </>
          )}
          {tab === "products" && (
            <>
              <p className="m-0 text-[20px] font-semibold">{t`You have no products yet`}</p>
              <p className="m-0 text-[15px] text-text_secondary">{t`The digital products you buy appear here, ready to open.`}</p>
            </>
          )}
          {tab === "history" && (
            <>
              <p className="m-0 text-[20px] font-semibold">{t`Nothing here yet`}</p>
              <p className="m-0 text-[15px] text-text_secondary">{t`The products you have opened appear here.`}</p>
            </>
          )}
          <Button variant="none" href="/category/latest" className="mt-3 inline-flex h-[40px] items-center rounded-full bg-primary px-6 text-[16px] font-bold text-white hover:bg-primary_hov" data-testid="library-empty-browse">
            {t`Browse the catalog`}
          </Button>
        </div>
      )}
      {list.length > 0 && (
        <div className="flex flex-wrap justify-evenly md:justify-start gap-4 md:gap-6" data-testid="library-grid">
          {list.map((product) => (
            <ProductBox key={product.pid} product={product} placement="library" />
          ))}
        </div>
      )}
      {pageCount > 1 && <Pagination page={page} count={pageCount} onChange={(p) => setSearchParams({ page: p > 1 ? p : null })} className="self-center" />}
    </div>
  )
}
